import { createAsyncThunk, createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { CommentType } from 'features/files-preview/ui/Comment'
import { getAIAssistantName } from 'features/user/model/selectors/userSelectors'
import _ from 'lodash'
import { assetsApi } from 'shared/api/assetsApi'
import { commentsApi } from 'shared/api/commentsApi'
import { request } from 'shared/api/request'
import { RootState } from 'shared/config/store'
import { AI_BOT_USER_ID, AI_TYPING_MESSAGE } from 'shared/constants/ai'
import { initWs } from 'shared/websocket/ws'

import { WSActions } from '../../../../shared/constants/common'
import { CREATING_NEW_ANNOTATION_MODE } from '../../ui/utils/annotationUtils'
import { CommentsFilterType, EnhancedItemSchema, FilesPreviewActiveTab, FilesPreviewModalType, FilesPreviewState, PreviewOpenActionType } from '../types/filesPreviewTypes'
import { publicBoardsApi } from '../../../../shared/api/publicBoardsApi'

const adapter = createEntityAdapter<EnhancedItemSchema>()

let loadMoreHandler

export const openFilesPreviewThunk = createAsyncThunk(
  'filesPreview/openFilesPreviewThunk',
  async function ({ items, currentItemId, totalItemsCount, currentBoardInfo, navigate, withPreventedClosing, modalType }:
    PreviewOpenActionType & { navigate: (path: string) => void, withPreventedClosing?: boolean, modalType?: FilesPreviewModalType }, { dispatch }) {
    dispatch(actions.openPreview({ items, currentItemId, totalItemsCount, currentBoardInfo, withPreventedClosing, modalType }))
    navigate(`?prv=${currentItemId}`)
  }
)

export const openFilesPreviewFromParamsThunk = createAsyncThunk(
  'filesPreview/openFilesPreviewFromParamsThunk',
  async function({ fileId, isPublicLink, self }: { fileId: string, isPublicLink?: boolean, self?: string }, { dispatch, getState }) {
    const state = getState() as RootState
    const userId = state.user?.user?.family_user_id

    let items: any[] = []
    let currentItemId: string | null = null

    if (isPublicLink) {
      const response = await request('GET', self, { preview: true, preview_jwt: true }, { host: null }) as any

      items = [response]
      dispatch(publicLinkFilesActions.setItems(items))
      currentItemId = response.id
    } else {
      const node = await assetsApi.getAssetInfo(userId, fileId, null, { preview: true, preview_jwt: true })

      items = [node]
      currentItemId = node.id
    }

    const totalItemsCount = 1
    const currentBoardInfo = null

    dispatch(actions.openPreview({ items, currentItemId, totalItemsCount, currentBoardInfo }))
  }
)

export const handleNextPhotoThunk = createAsyncThunk(
  'filesPreview/handleNextPhotoThunk',
  async function ({ currentIndex, callback }: { currentIndex: number, callback?: (nextItemId: string) => void }, { dispatch, getState }) {
    const state = getState() as RootState
    const itemsLength = state.filesPreview.ids.length

    dispatch(actions.setError(false))

    if (currentIndex + 1 === itemsLength && loadMoreHandler) {
      loadMoreHandler()
    }

    // dispatch(actions.setCurrentItemIndex(currentIndex + 1))
    dispatch(actions.setLoading(true))

    if (callback) {
      callback(state.filesPreview.ids[currentIndex + 1])
    }
  }
)

export const handlePrevPhotoThunk = createAsyncThunk(
  'filesPreview/handlePrevPhotoThunk',
  async function ({ currentIndex, callback }: { currentIndex: number, callback?: (prevItemId: string) => void }, { dispatch, getState }) {
    const state = getState() as RootState

    dispatch(actions.setError(false))
    // dispatch(actions.setCurrentItemIndex(currentIndex - 1))
    dispatch(actions.setLoading(true))

    if (callback) {
      callback(state.filesPreview.ids[currentIndex - 1])
    }
  }
)

export const createCommentThunk = createAsyncThunk(
  'filesPreview/createCommentThunk',
  async function({ nodeId, message, parent_id, annotation, mentions }: { nodeId: string, message: string, parent_id?: number, annotation?: any, mentions?: any }, { dispatch, getState }) {
    const state = getState() as RootState
    const familyID = state.user?.user?.family_user_id
    const comments = state.filesPreview.comments
    const userID = state.user?.user?.id || 0
    const sharedId = state.publicLinkFiles?.sharedId

    const data: any = {
      message,
    }

    if (parent_id) {
      data.parent_id = parent_id
    }

    if (annotation) {
      data.annotation = annotation
    }

    if (mentions) {
      data.mentions = mentions
    }

    const isAIMentioned = mentions?.some((mention: any) => mention.user_id === String(AI_BOT_USER_ID))

    const comment = await (sharedId
      ? commentsApi.createSharedComment(userID, sharedId, nodeId, data)
      : commentsApi.createComment(familyID, nodeId, data))

    let newComments = _.cloneDeep(comments)

    if (parent_id) {
      const commentIndex = newComments.findIndex((comment) => comment.id === parent_id)
      const commentToReply = newComments[commentIndex]

      if (commentToReply?.replies) {
        commentToReply.replies = [...commentToReply.replies, comment]
      } else {
        commentToReply.replies = [comment]
      }
    } else {
      newComments = [...newComments, comment]
    }

    if (isAIMentioned) {
      let parentStatus: 'open' | 'resolved' = 'open'
      if (parent_id) {
        const parentComment = newComments.find((c) => c.id === parent_id)
        parentStatus = (parentComment?.status as 'open' | 'resolved') || 'open'
      } else {
        parentStatus = (comment.status as 'open' | 'resolved') || 'open'
      }

      const tempAIReply: CommentType = {
        id: -Date.now(),
        message: AI_TYPING_MESSAGE,
        author_name: getAIAssistantName(state),
        author_id: String(AI_BOT_USER_ID),
        created: new Date().toISOString(),
        status: parentStatus,
        replies: []
      }

      if (parent_id) {
        const parentIndex = newComments.findIndex((c) => c.id === parent_id)
        if (parentIndex !== -1) {
          if (newComments[parentIndex].replies) {
            newComments[parentIndex].replies = [...newComments[parentIndex].replies, tempAIReply]
          } else {
            newComments[parentIndex].replies = [tempAIReply]
          }
        }
      } else {
        const commentIndex = newComments.findIndex((c) => c.id === comment.id)
        if (commentIndex !== -1) {
          if (newComments[commentIndex].replies) {
            newComments[commentIndex].replies = [...newComments[commentIndex].replies, tempAIReply]
          } else {
            newComments[commentIndex].replies = [tempAIReply]
          }
        }
      }
    }

    dispatch(actions.setComments(newComments))

    return { commentId: comment.id, isAIMentioned }
  }
)


let handleWSComment

export const subscribeToWSCommentsThunk = createAsyncThunk(
  'filesPreview/subscribeToCommentsThunk',
  async function (_, { dispatch, getState }) {
    const ws = initWs({ includeFamilyUserId: true })

    handleWSComment = async (message) => {
      const { action, resource, commentable_resource_info } = message.output
      const commentableResourceId = commentable_resource_info?.id.split(':')[0] || ''
      // Get current state to check file and shared status
      const currentState = getState() as RootState
      const currentFileId = currentState.filesPreview.ids[currentState.filesPreview.currentItemIndex]
      const commentsWaitingForAiReply = currentState.filesPreview.commentsWaitingForAiReply
      // Check if this comment belongs to the current file
      if (commentableResourceId !== currentFileId) {
        console.warn('Commentable resource ID does not match current file ID')
        return
      }

      const replies = (resource as any)._embedded?.replies || []
      const parentId = (resource as any).parent_id || null

      if (commentsWaitingForAiReply.includes(parentId) && resource.author_id === AI_BOT_USER_ID) {
        dispatch(replaceTemporaryAIReply({
          commentId: parentId,
          aiReply: resource
        }))

        dispatch(actions.removeCommentWaitingForAiReply(parentId))

        return
      }

      switch (action) {
      case WSActions.CREATED:
        // Add new comment or reply
        const newComment = {
          ...(resource as any),
          replies
        }
        dispatch(actions.addCommentFromWS({
          comment: newComment,
          parentId
        }))
        break

      case WSActions.CHANGED:
        // Update existing comment or reply
        const updatedComment = {
          ...(resource as any),
          replies
        }
        dispatch(actions.updateCommentFromWS({
          comment: updatedComment,
          parentId
        }))
        break

      case WSActions.DELETED:
        // Delete comment or reply
        dispatch(actions.deleteCommentFromWS({
          commentId: (resource as any).id,
          parentId
        }))
        break

      default:
        console.warn('Unknown comment action:', action)
      }
    }

    ws.addEventListener('comments', handleWSComment)
  }
)

export const unsubscribeToWSCommentsThunk = createAsyncThunk(
  'filesPreview/unsubscribeToCommentsThunk',
  async function() {
    const ws = initWs({ includeFamilyUserId: true })

    ws.removeEventListener('comments', handleWSComment)
  }
)

export const deleteCommentThunk = createAsyncThunk(
  'filesPreview/deleteCommentThunk',
  async function({ nodeId, commentId }: { nodeId: string, commentId: number }, { dispatch, getState }) {
    const state = getState() as RootState
    const familyID = state.user.user.family_user_id
    const sharedId = state.publicLinkFiles.sharedId
    const userID = state.user.user.id

    await (sharedId
      ? commentsApi.deleteSharedComment(userID, sharedId, nodeId, commentId)
      : commentsApi.deleteComment(familyID, nodeId, commentId))

    const deleteComment = (comments: CommentType[]): CommentType[] => {
      return comments.filter(comment => {
        if (comment.id === commentId) {
          return false
        }

        if (comment.replies) {
          comment.replies = deleteComment(comment.replies)
        }

        return true
      })
    }


    const newComments = deleteComment(_.cloneDeep(state.filesPreview.comments))

    dispatch(actions.setComments(newComments))
    dispatch(actions.setSelectedAnnotation(null))
  }
)

export const updateCommentThunk = createAsyncThunk(
  'filesPreview/updateCommentThunk',
  async function({ nodeId, commentId, message, mentions }: { nodeId: string, commentId: number, message: string, mentions?: any }, { dispatch, getState }) {
    const state = getState() as RootState
    const familyID = state.user.user.family_user_id
    const sharedId = state.publicLinkFiles.sharedId
    const userID = state.user.user.id
    let selectedAnnotation = state.filesPreview.selectedAnnotation

    const updatedComment = await (sharedId
      ? commentsApi.updateSharedComment(userID, sharedId, nodeId, commentId, { message, mentions })
      : commentsApi.updateComment(familyID, nodeId, commentId, { message, mentions }))
    const editComment = (comments: CommentType[]): CommentType[] => {
      return comments.map(comment => {
        if (comment.id === commentId) {
          return { ...comment, ...updatedComment }
        }

        if (comment.replies) {
          return { ...comment, replies: editComment(comment.replies) }
        }

        return comment
      })
    }

    const newComments = editComment(_.cloneDeep(state.filesPreview.comments))
    const selectedComment = newComments.find((comment) => comment.id === selectedAnnotation?.data?.id)
    dispatch(actions.setComments(newComments))
    if(selectedComment && selectedAnnotation) {
      selectedAnnotation = {
        ...selectedAnnotation,
        data: {
          ...selectedAnnotation.data,
          author_id: selectedComment.author_id,
          author_name: selectedComment.author_name,
          created: selectedComment.created,
          id: selectedComment.id,
          text: selectedComment.message
        }
      }
      dispatch(actions.setSelectedAnnotation(selectedAnnotation))
    }
  }
)

export const updateCommentStatusThunk = createAsyncThunk(
  'filesPreview/updateCommentStatusThunk',
  async function({ nodeId, commentId, status }: { nodeId: string, commentId: number, status: 'open' | 'resolved' }, { dispatch, getState }) {
    const state = getState() as RootState
    const familyID = state.user.user.family_user_id
    const sharedId = state.publicLinkFiles.sharedId
    const userID = state.user.user.id

    const updatedComment = await (sharedId
      ? commentsApi.updateSharedComment(userID, sharedId, nodeId, commentId, { status })
      : commentsApi.updateComment(familyID, nodeId, commentId, { status }))

    const updateCommentStatus = (comments: CommentType[]): CommentType[] => {
      return comments.map(comment => {
        if (comment.id === commentId) {
          const updatedParentComment = { ...comment, ...updatedComment }

          if (updatedParentComment.replies && updatedParentComment.replies.length > 0) {
            updatedParentComment.replies = updatedParentComment.replies.map(reply => ({
              ...reply,
              status: status
            }))
          }

          return updatedParentComment
        }

        if (comment.replies) {
          return { ...comment, replies: updateCommentStatus(comment.replies) }
        }

        return comment
      })
    }

    const newComments = updateCommentStatus(_.cloneDeep(state.filesPreview.comments))
    dispatch(actions.setComments(newComments))
  }
)

export const fetchFileDataThunk = createAsyncThunk(
  'filesPreview/fetchFileDataThunk',
  async function({ node, signal, isPublicLink }: { node: any, signal?: AbortSignal, isPublicLink?: boolean }, { dispatch, getState }) {
    const state = getState() as RootState
    const userID = state.user?.user?.family_user_id || 0
    const nodeId = node.id

    dispatch(actions.setTabsContentLoaded(false))

    try {
      if (isPublicLink) {
        let nodeLink = node?._links?.self?.href

        if (node?._links?.node?.href) {
          const urlTemplate = _.template(node._links.node.href, { interpolate: /\{(.+?)\}/g })
          nodeLink = urlTemplate({ node_id: nodeId })
        }

        if (!nodeLink) {
          return
        }

        const nodeResponse = await request('GET', nodeLink, {}, { host: null, signal }) as any

        const fieldsLink = nodeResponse._links?.fields?.href

        if (fieldsLink) {
          const fieldsResponse = await request('GET', fieldsLink, {}, { host: null, signal }) as any

          const useForRagFieldEntry = fieldsResponse._embedded.fields.find((field) => field?.field?.title === 'use_for_rag')
          const statusFieldEntry = fieldsResponse._embedded.fields.find((field) => field?.field?.title === 'status')
          const tagsFieldEntry = fieldsResponse._embedded.fields.find((field) => field?.field?.title === 'tags')
          const descriptionFieldEntry = fieldsResponse._embedded.fields.find((field) => field?.field?.title === 'description')

          const statusField = statusFieldEntry?.field
          const tagsField = tagsFieldEntry?.field
          const descriptionField = descriptionFieldEntry?.field
          const useForRagField = useForRagFieldEntry?.field
          

          const statuses = statusField?.options || []
          const tags = tagsField?.options || []
          const statusFieldValue = statusFieldEntry?.value as string[]
          const tagsFieldValue = tagsFieldEntry?.value as string[]
          const descriptionFieldValue = descriptionFieldEntry?.value ?? ''

          dispatch(actions.updateSystemFieldsValues({ status: statusFieldValue, tags: tagsFieldValue || [], description: descriptionFieldValue, use_for_rag: useForRagField?.value || null }))
          dispatch(actions.setSystemFieldsOptions({ status: statuses, tags }))
          dispatch(actions.setFieldsIds({
            statusFieldId: statusField?.id,
            tagsFieldId: tagsField?.id,
            descriptionFieldId: descriptionField?.id,
          }))
        }

        const commentsLink = nodeResponse._links?.comments?.href

        if (commentsLink) {
          const commentsResponse = await request('GET', commentsLink, {}, { host: null, signal })

          const comments = commentsResponse._embedded.comments.map((comment) => ({
            ...comment,
            replies: comment?._embedded?.replies ? comment._embedded.replies.map((reply) => ({
              ...reply,
            })) : [],
          }))

          dispatch(actions.setComments(comments))
        }

        return
      }


      const [assetInfo, assetFields, commentsResponse] = await Promise.all([
        assetsApi.getAssetInfo(userID, nodeId, signal),
        assetsApi.getAssetFields(userID, nodeId, signal),
        commentsApi.getCommentsByNodeId(userID, nodeId, signal)
      ])

      const statusFieldEntry = assetFields._embedded.fields.find((field) => field?.field?.title === 'status')
      const tagsFieldEntry = assetFields._embedded.fields.find((field) => field?.field?.title === 'tags')
      const descriptionFieldEntry = assetFields._embedded.fields.find((field) => field?.field?.title === 'description')
      const useForRagFieldValue = assetFields._embedded.fields.find((field) => field?.field?.title === 'use_for_rag')?.value as boolean

      const statusFieldValue = statusFieldEntry?.value as string[]
      const tagsFieldValue = tagsFieldEntry?.value as string[]
      const descriptionFieldValue = descriptionFieldEntry?.value ?? ''

      const statusFieldOptions = statusFieldEntry?.field?.options || []
      const tagsFieldOptions = tagsFieldEntry?.field?.options || []
      const statusFieldId = statusFieldEntry?.field?.id
      const tagsFieldId = tagsFieldEntry?.field?.id
      const descriptionFieldId = descriptionFieldEntry?.field?.id

      const comments = commentsResponse._embedded.comments.map((comment) => ({
        ...comment,
        replies: comment?._embedded?.replies ? comment._embedded.replies.map((reply) => ({
          ...reply,
        })) : [],
      }))

      dispatch(actions.setComments(comments))

      dispatch(actions.updateSystemFieldsValues({ status: statusFieldValue, tags: tagsFieldValue || [], description: descriptionFieldValue, use_for_rag: useForRagFieldValue }))
      dispatch(actions.setSystemFieldsOptions({ status: statusFieldOptions, tags: tagsFieldOptions }))
      dispatch(actions.setFieldsIds({
        statusFieldId,
        tagsFieldId,
        descriptionFieldId,
      }))

      dispatch(actions.setAiTags(assetInfo?.file_info?.recognized_labels || []))

    } catch (error) {
      console.log('error', error)
    } finally {
      dispatch(actions.setTabsContentLoaded(true))
    }
  }
)

export const fetchCommentsThunk = createAsyncThunk(
  'filesPreview/fetchCommentsThunk',
  async function({ node, signal, isPublicLink }: { node: any, signal?: AbortSignal, isPublicLink?: boolean }, { dispatch, getState }) {
    const state = getState() as RootState
    const userID = state.user?.user?.family_user_id || 0
    const nodeId = node.id

    if (isPublicLink) {
      const urlTemplate = _.template(node._links.node.href, { interpolate: /\{(.+?)\}/g })
      const nodeLink = urlTemplate({ node_id: nodeId })

      const nodeResponse = await request('GET', nodeLink, {}, { host: null, signal }) as any

      const commentsLink = nodeResponse._links.comments.href
      const commentsResponse = await request('GET', commentsLink, {}, { host: null, signal })

      const comments = commentsResponse._embedded.comments.map((comment) => ({
        ...comment,
        replies: comment?._embedded?.replies ? comment._embedded.replies.map((reply) => ({
          ...reply,
        })) : [],
      }))

      dispatch(actions.setComments(comments))

      return
    }

    const commentsResponse = await commentsApi.getCommentsByNodeId(userID, nodeId, signal)

    const comments = (commentsResponse as any)._embedded.comments.map((comment) => ({
      ...comment,
      replies: comment?._embedded?.replies ? comment._embedded.replies.map((reply) => ({
        ...reply,
      })) : [],
    }))

    dispatch(actions.setComments(comments))
  }
)

export const changePublicAssetTagsThunk = createAsyncThunk(
  'filesPreview/changePublicAssetTagsThunk',
  async (
    { userId, shareId, nodeId, fieldID, data, signal }:
    { userId: number, shareId: string, nodeId: string, fieldID: string, data: any, signal?: AbortSignal },
    { getState, dispatch }
  ) => {
    const rsp = await publicBoardsApi.updateBoardFields(userId, shareId, nodeId, fieldID, data, signal)
    const newValue = rsp?.value
    const state = getState() as RootState
    const currentNodeFields = state.publicLinkFiles.nodeFieldsById[nodeId]
    if (currentNodeFields) {
      const updated = {
        ...currentNodeFields,
        tags: {
          ...currentNodeFields.tags,
          value: newValue ?? data?.value ?? currentNodeFields.tags?.value ?? []
        }
      }
      const tagsFieldValue = updated?.tags?.value as string[]
      dispatch(actions.updateSystemFieldsValues({ tags: tagsFieldValue || [] }))
    }
  }
)

export const updateAssetDescriptionThunk = createAsyncThunk(
  "filesPreview/updateAssetDescriptionThunk",
  async ({ userId, nodeId, shareId, fieldID, description, callback }:
         { userId: number, nodeId: string, shareId: string, fieldID?: string | null, description: string, callback: () => void }) => {
    if (fieldID) {
      await publicBoardsApi.updateBoardFields(userId, shareId, nodeId, fieldID, { value: description })
    } else {
      await publicBoardsApi.updateBoardData(userId, shareId, nodeId, { description })
    }

    callback()
  }
)

export const updateAssetNameThunk = createAsyncThunk(
  "filesPreview/updateAssetNameThunk",
  async ({ userId, nodeId, shareId, name, callback }:
         { userId: number, nodeId: string, shareId: string, name: string, callback: () => void }) => {
    await publicBoardsApi.updateBoardData(userId, shareId, nodeId, { name })
    callback()
  }
)

export const changePublicAssetStatusThunk = createAsyncThunk(
  'filesPreview/changePublicAssetStatusThunk',
  async (
    { userId, shareId, nodeId, fieldID, statusId, signal }:
    { userId: number, shareId: string, nodeId: string, fieldID: string, statusId: string | null, signal?: AbortSignal },
    { getState, dispatch }
  ) => {
    if (statusId) {
      const rsp = await publicBoardsApi.updateBoardFields(userId, shareId, nodeId, fieldID, { value: statusId }, signal)
      const newValue = rsp?.value ?? statusId
      const state = getState() as RootState
      const currentNodeFields = state.publicLinkFiles.nodeFieldsById[nodeId]
      if (currentNodeFields) {
        const updated = {
          ...currentNodeFields,
          status: {
            ...currentNodeFields.status,
            value: newValue
          }
        }
        const statusFieldValue = updated?.status?.value as string[] | string | null
        dispatch(actions.updateSystemFieldsValues({ status: statusFieldValue || null }))
      }
    } else {
      await publicBoardsApi.deleteBoardFields(userId, shareId, nodeId, fieldID, signal)
      const state = getState() as RootState
      const currentNodeFields = state.publicLinkFiles.nodeFieldsById[nodeId]
      if (currentNodeFields) {
        const updated = {
          ...currentNodeFields,
          status: currentNodeFields.status ? { ...currentNodeFields.status, value: null } : null
        }
        const statusFieldValue = updated?.status?.value as string[] | string | null
        dispatch(actions.updateSystemFieldsValues({ status: statusFieldValue || null }))
      }
    }
  }
)

export const filesPreviewSelectors = adapter.getSelectors()

export enum TabsNames {
  INFO = 'info',
  COMMENTS = 'comments',
  VERSIONS = 'versions',
}

export const filesPreviewSlice = createSlice({
  name: 'filesPreview',
  initialState: adapter.getInitialState<FilesPreviewState>({
    currentItemIndex: 0,
    isModalOpened: false,
    loading: true,
    totalItemsCount: 0,
    loadingMore: false,
    hasError: false,
    aiTags: [],
    comments: [],
    commentsFilter: 'open',
    annotations: [],
    annotation: {},
    sharedId: null,
    isCommentsLoading: false,
    isShowAnnotationLayer: false,
    isShowAnnotationMarkers: false,
    isCreatingNewAnnotation: false,
    creatingNewAnnotationMode: null,
    isControlPanelVisible: false,
    selectedAnnotation: null,
    newAnnotation: null,
    tabsContentLoaded: true,
    currentBoardInfo: null,
    isPublicLink: false,
    systemFieldsValues: {
      status: null,
      tags: [],
      use_for_rag: null,
      description: '',
    },
    systemFieldsOptions: {
      status: [],
      tags: [],
    },
    fieldsIds: {
      statusFieldId: null,
      tagsFieldId: null,
      descriptionFieldId: null,
    },
    playerState: {
      currentTime: 0,
      duration: 0,
      volume: 1,
      isPlaying: false,
      isFullscreen: false,
      isMuted: false,
      isLooping: false,
      playbackRate: 1
    },
    activeTab: TabsNames.INFO,
    commentIdForScrollTo: null,
    replyingCommentId: null,
    withPreventedClosing: false,
    animatedCommentIds: [],
    overlayImageData: {
      url: '',
    },
    commentsWaitingForAiReply: [],
    modalType: 'files'
  }),
  reducers: {
    addItem: (state, action: PayloadAction<EnhancedItemSchema>) => {
      adapter.addOne(state, action.payload)
    },
    updateItem: (state, action) => {
      adapter.updateOne(state, {
        id: action.payload.id,
        changes: action.payload,
      })
    },
    deleteItem: (state, action: PayloadAction<string>) => {
      adapter.removeOne(state, action.payload)
    },
    setAllItems: (state, action) => {
      adapter.setAll(state, action.payload)
    },
    setTotalItemsCount: (state, action: PayloadAction<number>) => {
      state.totalItemsCount = action.payload
    },
    setCurrentItemIndexById: (state, action: PayloadAction<string>) => {
      const ids = adapter.getSelectors().selectIds(state)
      const indexOfItem = ids.indexOf(action.payload)

      state.currentItemIndex = indexOfItem
    },
    setCurrentItemIndex: (state, action) => {
      state.currentItemIndex = action.payload
    },
    increaseCurrentItemIndex: (state, action) => {
      state.currentItemIndex = state.currentItemIndex + action.payload
    },
    toggleModal: (state, action) => {
      state.isModalOpened = action.payload
    },
    openPreview: (state, action: PayloadAction<PreviewOpenActionType>) => {
      adapter.setAll(state, action.payload.items)
      state.totalItemsCount = action.payload.totalItemsCount
      state.currentBoardInfo = action.payload.currentBoardInfo || null
      state.commentIdForScrollTo = action.payload.commentIdForScrollTo || null
      state.withPreventedClosing = action.payload.withPreventedClosing || false
      state.modalType = action.payload.modalType || 'files'

      if (action.payload.activeTab) {
        state.activeTab = action.payload.activeTab
      }

      const ids = action.payload.items.map(item => item.id)
      const indexOfItem = ids.indexOf(action.payload.currentItemId)

      state.currentItemIndex = indexOfItem
      // isModalOpened больше не устанавливается здесь - теперь зависит только от URL
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setLoadingMore: (state, action: PayloadAction<boolean>) => {
      state.loadingMore = action.payload
    },
    setError: (state, action: PayloadAction<boolean>) => {
      state.hasError = action.payload
    },
    setAiTags: (state, action: PayloadAction<string[]>) => {
      state.aiTags = action.payload
    },
    setSystemFieldsValues: (state, action: PayloadAction<{ status?: string[] | string | null, tags?: string[] | string | null, description?: string | null }>) => {
      state.systemFieldsValues = {
        ...state.systemFieldsValues,
        ...action.payload,
      }
    },
    setFieldsIds: (state, action: PayloadAction<{ statusFieldId?: string | null, tagsFieldId?: string | null, descriptionFieldId?: string | null }>) => {
      const { statusFieldId, tagsFieldId, descriptionFieldId } = action.payload

      if (statusFieldId !== undefined) {
        state.fieldsIds.statusFieldId = statusFieldId
      }

      if (tagsFieldId !== undefined) {
        state.fieldsIds.tagsFieldId = tagsFieldId
      }

      if (descriptionFieldId !== undefined) {
        state.fieldsIds.descriptionFieldId = descriptionFieldId
      }
    },
    setComments: (state, action: PayloadAction<any[]>) => {
      state.comments = action.payload
    },
    addReplyToCommentById: (state, action: PayloadAction<{ parentCommentId: number, replyComment: CommentType }>) => {
      const { parentCommentId, replyComment } = action.payload
      state.comments = state.comments.map(comment => {
        if(comment.id === parentCommentId) {
          return { ...comment, replies: comment.replies ? [...comment.replies, replyComment] : [replyComment] }
        }
        return comment
      })
    },
    setIsPublicLink: (state, action: PayloadAction<boolean>) => {
      state.isPublicLink = action.payload
    },
    updateSystemFieldsValues: (state, action: PayloadAction<{ status?: string[] | string | null, tags?: string[] | string | null, description?: string | null, use_for_rag?: boolean | null }>) => {
      state.systemFieldsValues = {
        ...state.systemFieldsValues,
        ...action.payload,
      }
    },
    setTabsContentLoaded: (state, action: PayloadAction<boolean>) => {
      state.tabsContentLoaded = action.payload
    },
    updatePlayerState: (state, action: PayloadAction<Partial<FilesPreviewState['playerState']>>) => {
      state.playerState = {
        ...state.playerState,
        ...action.payload
      }
    },
    setPlayerCurrentTime: (state, action: PayloadAction<number>) => {
      state.playerState.currentTime = action.payload
    },
    setPlayerDuration: (state, action: PayloadAction<number>) => {
      state.playerState.duration = action.payload
    },
    setPlayerIsPlaying: (state, action: PayloadAction<boolean>) => {
      state.playerState.isPlaying = action.payload
    },
    setPlayerVolume: (state, action: PayloadAction<number>) => {
      state.playerState.volume = action.payload
    },
    setPlayerIsMuted: (state, action: PayloadAction<boolean>) => {
      state.playerState.isMuted = action.payload
    },
    setPlayerIsFullscreen: (state, action: PayloadAction<boolean>) => {
      state.playerState.isFullscreen = action.payload
    },
    setPlayerIsLooping: (state, action: PayloadAction<boolean>) => {
      state.playerState.isLooping = action.payload
    },
    setPlayerPlaybackRate: (state, action: PayloadAction<number>) => {
      state.playerState.playbackRate = action.payload
    },
    setActiveTab: (state, action: PayloadAction<FilesPreviewActiveTab>) => {
      state.activeTab = action.payload
    },
    setCommentIdForScrollTo: (state, action: PayloadAction<string | null>) => {
      state.commentIdForScrollTo = action.payload
    },
    setCommentsWaitingForAiReply: (state, action: PayloadAction<string[]>) => {
      state.commentsWaitingForAiReply = action.payload
    },
    addCommentWaitingForAiReply: (state, action: PayloadAction<string>) => {
      if (!state.commentsWaitingForAiReply.includes(action.payload)) {
        state.commentsWaitingForAiReply.push(action.payload)
      }
    },
    removeCommentWaitingForAiReply: (state, action: PayloadAction<string>) => {
      state.commentsWaitingForAiReply = state.commentsWaitingForAiReply.filter(id => id !== action.payload)
    },
    resetState: (state) => {
      state.selectedAnnotation = null
      state.isModalOpened = false
      state.loading = true
      state.loadingMore = false
      state.hasError = false
      state.aiTags = []
      state.isShowAnnotationLayer = false
      state.isShowAnnotationMarkers = false
      state.isCreatingNewAnnotation = false
      state.creatingNewAnnotationMode = null
      state.commentIdForScrollTo = null
      state.isControlPanelVisible = false
      state.newAnnotation = null
      state.comments = []
      state.annotations = []
      state.activeTab = TabsNames.INFO
      state.systemFieldsValues = {
        status: null,
        tags: [],
        description: '',
        use_for_rag: null,
      }
      state.fieldsIds = {
        statusFieldId: null,
        tagsFieldId: null,
        descriptionFieldId: null,
      }
      state.overlayImageData = {
        url: '',
        type: undefined,
        versionId: undefined,
        versionData: undefined,
        sourceCommentId: undefined
      }
      state.playerState = {
        currentTime: 0,
        duration: 0,
        volume: 1,
        isPlaying: false,
        isFullscreen: false,
        isMuted: false,
        isLooping: false,
        playbackRate: 1
      }

      loadMoreHandler = null
    },

    // Annotations
    setSelectedAnnotation: (state, action: PayloadAction<any | null>) => {
      state.selectedAnnotation = action.payload
    },
    setNewAnnotation: (state, action: PayloadAction<string>) => {
      state.newAnnotation = action.payload
    },
    setIsShowAnnotationLayer: (state, action: PayloadAction<boolean>) => {
      state.isShowAnnotationLayer = action.payload
    },
    setIsShowAnnotationMarkersLayer: (state, action: PayloadAction<boolean>) => {
      state.isShowAnnotationMarkers = action.payload
    },
    setIsCreatingNewAnnotation: (state, action: PayloadAction<boolean>) => {
      state.isCreatingNewAnnotation = action.payload
    },
    setCreatingNewAnnotationMode: (state, action: PayloadAction<CREATING_NEW_ANNOTATION_MODE>) => {
      state.creatingNewAnnotationMode = action.payload
    },
    setIsControlPanelVisible: (state, action: PayloadAction<boolean>) => {
      state.isControlPanelVisible = action.payload
    },
    setReplyingCommentId: (state, action: PayloadAction<string | null>) => {
      state.replyingCommentId = action.payload
    },
    replaceTemporaryAIReply: (state, action: PayloadAction<{ commentId: number, aiReply: CommentType }>) => {
      const { aiReply } = action.payload

      const replaceTypingMessage = (comments: CommentType[]): CommentType[] => {
        return comments.map(comment => {
          if (comment.replies && comment.replies.length > 0) {
            comment.replies = comment.replies.map(reply => {
              if (reply.author_id === String(AI_BOT_USER_ID) && reply.message === AI_TYPING_MESSAGE) {
                return {
                  ...aiReply,
                  status: reply.status,
                  replies: reply.replies || [],
                  isNewAIMessage: true
                }
              }
              return reply
            })

            comment.replies = replaceTypingMessage(comment.replies)
          }
          return comment
        })
      }

      state.comments = replaceTypingMessage(state.comments)
    },
    addAnimatedCommentId: (state, action: PayloadAction<{ commentId: string }>) => {
      if (!state.animatedCommentIds.includes(action.payload.commentId)) {
        state.animatedCommentIds.push(action.payload.commentId)
      }
    },
    resetAIMessageAnimationFlag: (state, action: PayloadAction<{ commentId: number }>) => {
      const { commentId } = action.payload

      const resetFlag = (comments: CommentType[]): CommentType[] => {
        return comments.map(comment => {
          if (comment.replies && comment.replies.length > 0) {
            comment.replies = comment.replies.map(reply => {
              if (reply.id === commentId && reply.author_id === String(AI_BOT_USER_ID)) {
                return {
                  ...reply,
                  isNewAIMessage: false
                }
              }
              return reply
            })

            comment.replies = resetFlag(comment.replies)
          }
          return comment
        })
      }

      state.comments = resetFlag(state.comments)
    },
    setSystemFieldsOptions: (state, action: PayloadAction<{ status: any[], tags: any[] }>) => {
      state.systemFieldsOptions = action.payload
    },
    setOverlayImageData: (state, action: PayloadAction<{ url: string, type?: 'ai' | 'version', versionId?: string, versionData?: any, sourceCommentId?: string, currentNodeData?: any }>) => {
      state.overlayImageData = action.payload
    },
    clearOverlayImageData: (state) => {
      state.overlayImageData = {
        url: '',
        type: undefined,
        versionId: undefined,
        versionData: undefined,
        sourceCommentId: undefined
      }
    },
    updatePreviewWithBackgroundData: (state, action: PayloadAction<{ items: any[], currentIndex: number, totalItemsCount: number }>) => {
      const { items, currentIndex, totalItemsCount } = action.payload
      adapter.setAll(state, items)
      state.currentItemIndex = currentIndex
      state.totalItemsCount = totalItemsCount
    },
    // WebSocket comment actions
    addCommentFromWS: (state, action: PayloadAction<{ comment: CommentType, parentId?: number }>) => {
      const { comment, parentId } = action.payload

      if (parentId) {
        // Add reply to existing comment
        const parentComment = state.comments.find(c => c.id === parentId)
        if (parentComment) {
          if (parentComment.replies) {
            parentComment.replies.push(comment)
          } else {
            parentComment.replies = [comment]
          }
        }
      } else {
        // Add new top-level comment
        state.comments.push(comment)
      }
    },
    updateCommentFromWS: (state, action: PayloadAction<{ comment: CommentType, parentId?: number }>) => {
      const { comment, parentId } = action.payload

      const updateCommentInList = (comments: CommentType[], commentToUpdate: CommentType) => {
        const index = comments.findIndex(c => c.id === commentToUpdate.id)
        if (index !== -1) {
          comments[index] = commentToUpdate
        }
      }

      if (parentId) {
        const parentComment = state.comments.find(c => c.id === parentId)
        if (parentComment && parentComment.replies) {
          updateCommentInList(parentComment.replies, comment)
        }
      } else {
        updateCommentInList(state.comments, comment)
      }
    },
    deleteCommentFromWS: (state, action: PayloadAction<{ commentId: number, parentId?: number }>) => {
      const { commentId, parentId } = action.payload
      if (parentId) {
        // Delete reply from existing comment
        const parentComment = state.comments.find(c => c.id === parentId)
        if (parentComment && parentComment.replies) {
          parentComment.replies = parentComment.replies.filter(r => r.id !== commentId)
        }
      } else {
        // Delete top-level comment
        state.comments = state.comments.filter(c => c.id !== commentId)
      }
    },
    setCommentsFilter: (state, action: PayloadAction<CommentsFilterType>) => {
      state.commentsFilter = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCommentsThunk.pending, (state) => {
        state.isCommentsLoading = true
      }).addCase(fetchCommentsThunk.fulfilled, (state) => {
        state.isCommentsLoading = false
      }).addCase(fetchCommentsThunk.rejected, (state) => {
        state.isCommentsLoading = false
      })
  }
})

const {
  reducer, actions
} = filesPreviewSlice

export { actions as filesPreviewActions, reducer as filesPreviewReducer }

export const {
  addReplyToCommentById,
  setSelectedAnnotation,
  setComments,
  replaceTemporaryAIReply,
  resetAIMessageAnimationFlag,
  addAnimatedCommentId,
  addCommentFromWS,
  updateCommentFromWS,
  deleteCommentFromWS,
  setCommentsFilter
} = filesPreviewSlice.actions
